import { useAuth } from '@/components/auth/authProvider';
import { isInternalUser } from '@/components/auth/RequireIsInternalUser';
import usePermissions from '@/hooks/usePermissions';
import { OrganizationNode } from '@/types/Organizations';
import { Box, SelectInputNative } from '@palmetto/palmetto-components';
import { useCallback, useMemo, useState } from 'react';

import { ProgramType, ProgramTypeLabels, UserPermissions } from 'types';

import { OrganizationsWithOverrides } from './OrganizationsWithOverrides';
import { UnderwritingSettings } from './UnderwritingSettings';

interface UnderwritingProps {
  organization: OrganizationNode;
}

export function Underwriting({ organization }: UnderwritingProps) {
  const permissions = usePermissions();
  const { claims } = useAuth();
  const isFicoAdmin =
    isInternalUser(claims, true) && (permissions?.includes(UserPermissions.lightReachFicoAdmin) ?? false);

  const [programType, setProgramType] = useState<ProgramType>(
    ((organization.programTypes?.find((pt) => pt !== ProgramType.doePr) ??
      organization.programTypes?.[0]) as ProgramType) ?? ProgramType.solar,
  );

  // doePr does not use the standard underwriting settings system and should never have settings created for it
  const programTypeOptions = useMemo(
    () =>
      (organization.programTypes ?? [])
        .filter((pt) => pt !== ProgramType.doePr)
        .map((pt) => ({
          label: ProgramTypeLabels[pt as ProgramType],
          value: pt,
        })),
    [organization.programTypes],
  );

  const handleProgramTypeChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    setProgramType(event.target.value as ProgramType);
  }, []);

  return (
    <Box padding="lg 0">
      {programTypeOptions.length > 1 && (
        <Box maxWidth="2xl" margin="0 0 lg 0">
          <SelectInputNative
            id="underwriting-program-type"
            label="Program Type"
            value={programType}
            onChange={handleProgramTypeChange}
            options={programTypeOptions}
          />
        </Box>
      )}
      <UnderwritingSettings organization={organization} programType={programType} isFicoAdmin={isFicoAdmin} />
      {/* Keyed so the lazy query's result and error reset when the org or program type changes. */}
      <OrganizationsWithOverrides
        key={`${organization.alias}-${programType}`}
        alias={organization.alias}
        programType={programType}
      />
    </Box>
  );
}
